import type { ItemSpecs } from './types';

/**
 * Antemăsurătoarea (lista de cantități, devizul, necesarul pe hârtie) citită
 * automat și împărțită pe categorii, ca să devină cereri de ofertă. Nimic nu
 * pleacă singur: omul vede pozițiile, le corectează și face cererile.
 */

/** O poziție citită din document. */
export interface BoqLine {
  /** codul articolului din document (ex. „CA04B1”); '' dacă nu are */
  code: string;
  desc: string;
  /** null = documentul nu spune cantitatea */
  qty: number | null;
  unit: string;
  /** una din categoriile firmei, sau „Altele” */
  category: string;
  /** chirie: câte zile, dacă documentul spune; null = nu spune / cumpărare */
  rentDays: number | null;
  specs: ItemSpecs | null;
  /** de unde vine sau ce e de verificat; '' dacă nimic */
  note: string;
}

export interface BoqExtraction {
  /** numele lucrării, din antetul documentului; '' dacă nu reiese */
  title: string;
  lines: BoqLine[];
  /** ce trebuie să știe omul: pagini ilizibile, tabele tăiate */
  warnings: string[];
}

/** Categoria pozițiilor care nu se potrivesc cu nicio categorie a firmei. */
export const BOQ_OTHER = 'Altele';

/** Peste atât, documentul e prea mare pentru o singură citire. */
export const BOQ_MAX_LINES = 400;

/** Tipurile acceptate la încărcare, pentru `accept` din browser. */
export const BOQ_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,.txt';

/** Cel mult atât de mare poate fi fișierul. */
export const BOQ_MAX_BYTES = 25 * 1024 * 1024;

/** Fără diacritice și majuscule: „ARMATURA” e aceeași categorie cu „Armătură”. */
const plainCat = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/**
 * Categoriile în care se împarte antemăsurătoarea: cele folosite de firmă, apoi
 * cele propuse pentru meseria ei care lipsesc. Doar cele folosite ar trimite la
 * „Altele” tot ce n-a mai cerut firma până acum.
 */
export function boqCategories(used: string[], suggested: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>([plainCat(BOQ_OTHER)]);
  for (const c of [...used, ...suggested]) {
    const k = plainCat(c);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(c.trim());
  }
  return out;
}

/** Pozițiile, pe categorii, în ordinea în care apar prima dată în document. */
export function boqGroups(lines: BoqLine[]): { category: string; lines: BoqLine[] }[] {
  const out = new Map<string, BoqLine[]>();
  for (const l of lines) {
    const list = out.get(l.category) ?? [];
    list.push(l);
    out.set(l.category, list);
  }
  // „Altele” la urmă: acolo e ce trebuie împărțit de mână
  return [...out.entries()]
    .map(([category, ls]) => ({ category, lines: ls }))
    .sort((a, b) => Number(a.category === BOQ_OTHER) - Number(b.category === BOQ_OTHER));
}

/* ---------- textele citite, pentru om (Ticket 08: P2, P12) ---------- */

/** Câmpurile din răspunsul modelului: n-au ce căuta în ce citește omul. */
const FIELDS = 'qty|unit|category|rentDays|heights|thickness|code|note|title|desc';

/**
 * Avertismentul fără numele câmpurilor și valorile lor („durata; rentDays = 0.” → „durata.”).
 * Rămâne gol dacă era doar atât.
 */
export function plainWarning(w: string): string {
  const out = w
    .replace(new RegExp(String.raw`\s*\([^()]*\b(?:${FIELDS})\b[^()]*\)`, 'g'), '')
    .replace(
      new RegExp(
        String.raw`[;,]?\s*(?:și\s+)?[„"]?\b(?:${FIELDS})\b[”"]?\s*(?:=|:)\s*(?:\[[^\]]*\]|"[^"]*"|-?\d+(?:[.,]\d+)?|\w+)`,
        'g',
      ),
      '',
    )
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,;:])/g, '$1')
    .replace(/[;,:]+\s*(?=[.]|$)/g, '')
    .replace(/^[\s;,:.]+/, '')
    .trim();
  return /\p{L}/u.test(out) ? out : '';
}

/** Cuvinte cu care nu se termină un titlu tăiat. */
const TAIL = new Set(['din', 'de', 'la', 'cu', 'și', 'pe', 'în', 'a', 'al', 'ale', 'pentru', 'si', 'in']);

/**
 * Titlul cererii făcute din antemăsurătoare: scurt și ușor de citit în liste (cel mult `max`
 * caractere). Subtitlul lung al documentului („Bloc P+4, Str. Viilor nr. 42, Brașov – infrastructură…”)
 * se oprește la prima liniuță, apoi la prima virgulă; altfel se taie la un cuvânt întreg.
 */
export function shortBoqTitle(title: string, max = 40): string {
  const s = title.replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  let first = s.split(/\s+[–—-]\s+/)[0]!.trim();
  if (first.includes(',')) first = first.split(',')[0]!.trim();
  if (first && first.length <= max) return first;
  const words: string[] = [];
  for (const w of s.split(' ')) {
    if ([...words, w].join(' ').length > max) break;
    words.push(w);
  }
  while (words.length > 1 && TAIL.has(words[words.length - 1]!.toLowerCase())) words.pop();
  return words.join(' ').replace(/[,;:–—-]+$/, '').trim();
}
